/**
 * ReturningClientHero — personalized welcome-back banner on the homepage.
 *
 * When a signed-in client lands on the homepage we greet them by name,
 * show what treatment they last had, and suggest their next visit based
 * on a treatment-specific cadence. One tap → prefilled booking flow.
 *
 * Flow:
 *   1. Read `client_token` from local/sessionStorage.
 *   2. GET /api/client/profile → { first_name, email }
 *   3. GET /api/client/last-booking?email=... → { services, days_ago, ...}
 *   4. Match the first service's treatment family to a cadence (days).
 *   5. Render a warm gold hero with rebook CTA.
 *
 * Silent-fail: if any step returns nothing we render nothing — the
 * regular homepage hero shows underneath as usual.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, Calendar, Sparkles, Clock, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// localStorage key that persists the "remind me later" snooze. Value is
// either a millisecond timestamp (until when the banner stays hidden)
// or the string "never" (permanently muted for this browser).
const SNOOZE_KEY = "kl_rc_hero_snooze_until";

function readSnooze() {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    if (!raw) return { active: false };
    if (raw === "never") return { active: true, forever: true };
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return { active: false };
    if (Date.now() < ts) return { active: true, until: ts };
    // Expired snooze — clean up so the banner shows on next visit.
    localStorage.removeItem(SNOOZE_KEY);
    return { active: false };
  } catch {
    return { active: false };
  }
}

function writeSnooze(days) {
  try {
    if (days === "never") localStorage.setItem(SNOOZE_KEY, "never");
    else localStorage.setItem(SNOOZE_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
  } catch { /* silent */ }
}

// Treatment cadence (days between visits) keyed by lowercase substring.
// Order matters — more-specific / higher-priority keys first. Neuromodulator
// keys are checked BEFORE "lip"/"filler" so "Lip Flip - Neuromodulator"
// resolves to Botox cadence (100d) instead of filler cadence (365d).
const CADENCE = [
  { keys: ["neuromodulator", "toxin", "dysport", "botox"],   days: 100 }, // ~3.5 mo
  { keys: ["baby glow"],                                     days: 56 },  // 8 wk maintenance
  { keys: ["skin booster", "redensity", "polynuc", "profhilo"], days: 56 },
  { keys: ["hydrafacial", "hydra facial"],                   days: 42 },  // 6 wk
  { keys: ["prp", "prf"],                                    days: 30 },
  { keys: ["microneed", "rejuvapen", "rf micro"],            days: 28 },
  { keys: ["laser"],                                         days: 42 },
  { keys: ["sculptra"],                                      days: 365 },
  { keys: ["filler", "lip", "cheek"],                        days: 365 },
  { keys: ["facial"],                                        days: 56 },
];

function pickCadence(serviceName) {
  const n = (serviceName || "").toLowerCase();
  for (const c of CADENCE) {
    if (c.keys.some((k) => n.includes(k))) return c.days;
  }
  return 120; // sensible default
}

function readClientToken() {
  try {
    return (
      localStorage.getItem("client_token") ||
      sessionStorage.getItem("client_token") ||
      null
    );
  } catch {
    return null;
  }
}

// Square hosted booking sites keyed by location id — same URLs already
// exposed in InlineBookingFlow's "BOOK DIRECT — SQUARE" green banner.
// When the returning-client hero already knows the client's preferred
// clinic, sending them here is dramatically faster than opening our
// internal picker (which asks for treatment + date + time all over
// again).
const SQUARE_DIRECT_URLS = {
  "1": "https://precision-nurse-injectionist.square.site/",
  "2": "https://karine-lauzon-rn.square.site/",
  "3": "https://karine-lauzon-rn-ottawa.square.site/",
};

// Fetch the very next bookable slot for the client's preferred location.
// Uses the same public endpoints the booking flow uses — no new backend
// wiring required. Silent-fails on any hiccup: the hero still renders with
// the "Rebook in 1 tap" button, which walks the client through picking a
// date manually. If a slot IS found we upgrade the CTA to a one-tap reserve.
async function fetchNextSlot({ locationId, serviceDurationMinutes = 30 }) {
  if (!locationId) return null;
  try {
    const datesRes = await axios.get(
      `${API}/api/square/available-dates/${locationId}`,
      { timeout: 10000 },
    );
    const dates = (datesRes.data && datesRes.data.available_dates) || [];
    if (dates.length === 0) return null;
    // The endpoint returns objects: { date, day_name, formatted }. Grab the ISO date.
    const firstDate = typeof dates[0] === "string" ? dates[0] : dates[0]?.date;
    if (!firstDate) return null;

    const timesRes = await axios.post(
      `${API}/api/square/availability`,
      {
        location_id: locationId,
        date: firstDate,
        service_duration_minutes: serviceDurationMinutes,
      },
      { timeout: 12000 },
    );
    const times = (timesRes.data && timesRes.data.available_times) || [];
    if (times.length === 0) return null;
    // Times come back as "HH:MM" strings (local ET). Build an ISO by
    // combining with the date so downstream formatting is clean.
    const firstTime = times[0].start_time;
    return {
      date: firstDate,
      time: firstTime,
      iso: `${firstDate}T${firstTime}:00`,
      locationName: datesRes.data.location_name || "",
    };
  } catch {
    return null;
  }
}

function formatSlotPill({ date, iso }) {
  try {
    // iso is a local wall-clock string like "2026-07-08T14:30:00" (no TZ).
    // Parse the pieces manually so we don't get pushed into UTC and shift
    // by 4h in the display.
    const d = iso ? new Date(iso) : new Date(`${date}T00:00:00`);
    const weekday = d.toLocaleDateString("en-US", { weekday: "short" });
    const monthDay = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    let timeLabel = "";
    if (iso) {
      // Take the raw HH:MM from the iso string to avoid TZ math surprises.
      const hhmm = iso.slice(11, 16); // "14:30"
      const [hStr, mStr] = hhmm.split(":");
      const h = parseInt(hStr, 10);
      const m = parseInt(mStr, 10);
      const ampm = h >= 12 ? "PM" : "AM";
      const h12 = ((h + 11) % 12) + 1;
      timeLabel = `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
    }
    const today = new Date();
    const y = today.getFullYear();
    const mo = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const todayStr = `${y}-${mo}-${day}`;
    const tmr = new Date(today); tmr.setDate(today.getDate() + 1);
    const tmrStr = `${tmr.getFullYear()}-${String(tmr.getMonth() + 1).padStart(2, "0")}-${String(tmr.getDate()).padStart(2, "0")}`;
    if (date === todayStr) return `Today · ${timeLabel}`;
    if (date === tmrStr) return `Tomorrow · ${timeLabel}`;
    return `${weekday} ${monthDay} · ${timeLabel}`;
  } catch {
    return "";
  }
}

export default function ReturningClientHero() {
  const [state, setState] = useState({ ready: false, show: false });
  const [showSnoozeMenu, setShowSnoozeMenu] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // Respect any active "remind me later" window first — no API
      // calls, no flicker. Feb 24 2026 Karine ask.
      const snooze = readSnooze();
      if (snooze.active) return;
      const token = readClientToken();
      if (!token) return;
      try {
        // 1) Profile
        const profRes = await axios.get(`${API}/api/client/profile`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000,
        });
        const profile = profRes.data || {};
        const email = (profile.email || "").trim();
        const firstName = (profile.first_name || "").trim();
        if (!email) return;

        // 2) Last booking
        const lbRes = await axios.get(
          `${API}/api/client/last-booking`,
          { params: { email }, timeout: 8000 },
        );
        const lb = lbRes.data;
        if (!lb || !lb.found || !lb.last) return;
        const services = lb.last.services || [];
        if (services.length === 0) return;
        const firstSvc = services[0];
        const serviceName = firstSvc?.name || firstSvc?.title || "your treatment";
        const daysAgo = Number.isFinite(lb.last.days_ago) ? lb.last.days_ago : null;
        const locationName = lb.last.location_name || "";
        if (daysAgo == null) return;

        // 3) Cadence math
        const cadenceDays = pickCadence(serviceName);
        const daysUntilDue = cadenceDays - daysAgo;
        const overdue = daysUntilDue < 0;
        const dueSoon = daysUntilDue >= 0 && daysUntilDue <= 21;
        const showNow = overdue || dueSoon;
        if (!showNow) return;

        if (!cancelled) {
          setState({
            ready: true,
            show: true,
            firstName,
            serviceName,
            daysAgo,
            daysUntilDue,
            overdue,
            locationName,
            locationId: lb.last.location_id || "",
            slot: null,
          });
        }

        // Fire-and-forget: try to grab the very next available slot at
        // their usual location. If we find one we upgrade the CTA from
        // "Rebook in 1 tap" to a real slot pill they can reserve directly.
        try {
          const slot = await fetchNextSlot({
            locationId: lb.last.location_id || "",
            serviceDurationMinutes: 30,
          });
          if (slot && !cancelled) {
            setState((s) => ({ ...s, slot }));
          }
        } catch { /* silent */ }
      } catch {
        // silent — non-signed-in / no history / API hiccup → just render nothing
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  if (!state.show) return null;

  const { firstName, serviceName, daysAgo, daysUntilDue, overdue, locationName, locationId, slot } = state;
  const dueCopy = overdue
    ? `You're ${Math.abs(daysUntilDue)} day${Math.abs(daysUntilDue) === 1 ? "" : "s"} past your usual refresh`
    : daysUntilDue === 0
    ? "Your next visit is due today"
    : `Your next visit is due in ${daysUntilDue} day${daysUntilDue === 1 ? "" : "s"}`;

  const openRebook = () => {
    // Karine (Jul 2026 UX fix): If we already know the client's preferred
    // clinic (location_id from their last booking), send them straight to
    // Square's hosted booking site for that clinic. This bypasses our
    // internal picker (which was still asking for treatment + location
    // + date + time even after showing "Next opening · Mon Jul 27").
    // Square's site handles the whole picker natively.
    const squareUrl = locationId ? SQUARE_DIRECT_URLS[locationId] : null;
    if (squareUrl) {
      try {
        window.open(squareUrl, "_blank", "noopener,noreferrer");
      } catch {
        // Pop-up blocker → fall through to same-tab redirect
        window.location.assign(squareUrl);
      }
      return;
    }
    // Fallback — no known location yet, open homepage booking modal via
    // the existing `open_booking` handoff (see HomePage.jsx line ~162).
    // Also stashes the resolved slot for InlineBookingFlow to preselect.
    try {
      localStorage.setItem("open_booking", "true");
      if (locationId || slot?.date || slot?.iso) {
        localStorage.setItem(
          "open_booking_prefill",
          JSON.stringify({
            location_id: locationId || "",
            date: slot?.date || "",
            time_iso: slot?.iso || "",
            time: slot?.time || "",
            source: "returning_client_hero",
          }),
        );
      }
    } catch { /* noop */ }
    window.location.assign("/");
  };

  const slotPill = slot ? formatSlotPill(slot) : null;

  return (
    <section
      className="w-full"
      data-testid="returning-client-hero"
      style={{
        background:
          "linear-gradient(135deg, #fef8ec 0%, #fbeecb 45%, #f3dfa4 100%)",
        borderBottom: "1px solid rgba(184,137,62,0.25)",
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        {/* Left: icon medallion */}
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 shadow-md"
          style={{ background: "linear-gradient(135deg,#f5c97a,#b8893e)" }}
          aria-hidden="true"
        >
          <Sparkles className="w-5 h-5 text-white" />
        </div>

        {/* Middle: greeting + smart line */}
        <div className="flex-1 min-w-0">
          <p
            className="text-[11px] tracking-widest font-bold uppercase"
            style={{ color: "#8a6516" }}
          >
            Welcome back{firstName ? `, ${firstName}` : ""}
          </p>
          <h3
            className="text-lg sm:text-xl leading-snug text-zinc-900 mt-0.5"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            <span className="font-semibold">{dueCopy}</span>
            <span className="text-zinc-700 font-normal"> · Last visit: {serviceName}{locationName ? ` at ${locationName}` : ""} · {daysAgo} day{daysAgo === 1 ? "" : "s"} ago</span>
          </h3>
        </div>

        {/* Right: CTA cluster */}
        <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto flex-wrap">
          {slotPill && (
            <span
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11.5px] font-semibold border shadow-sm"
              style={{
                background: "rgba(255,255,255,0.85)",
                color: "#4a3200",
                borderColor: "rgba(184,137,62,0.5)",
              }}
              data-testid="returning-client-next-slot"
              title={`Next opening at ${locationName || "your clinic"} — ${slotPill}`}
            >
              <Clock className="w-3.5 h-3.5" style={{ color: "#b8893e" }} />
              Next opening{locationName ? ` at ${locationName}` : ""} · {slotPill}
            </span>
          )}
          <button
            type="button"
            onClick={openRebook}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-white text-sm font-semibold shadow-md hover:shadow-lg transition-shadow"
            style={{ background: "linear-gradient(135deg,#f5c97a 0%,#d4a55a 55%,#b8893e 100%)", color: "#1a0f06" }}
            data-testid="returning-client-rebook-btn"
          >
            <Calendar className="w-4 h-4" />
            {slotPill
              ? `Reserve at ${locationName || "your clinic"}`
              : `Rebook${locationName ? ` at ${locationName}` : " in 1 tap"}`}
            <ArrowRight className="w-4 h-4" />
          </button>
          {/* Dismiss with a "remind me in" chooser. Feb 24 2026 Karine
              ask — clients wanted more control than a permanent hide.
              Three options: 10 days · 30 days · Never. Selection stored
              in localStorage (see SNOOZE_KEY); on the next homepage load
              the banner stays hidden until the timer elapses. */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowSnoozeMenu((v) => !v)}
              className="text-xs text-zinc-500 hover:text-zinc-800 px-2 inline-flex items-center gap-1"
              aria-label="Dismiss welcome banner"
              aria-expanded={showSnoozeMenu}
              data-testid="returning-client-dismiss"
            >
              Dismiss
            </button>

            {showSnoozeMenu && (
              <div
                className="absolute right-0 top-full mt-2 w-56 rounded-xl bg-white shadow-xl border border-amber-200 p-3 z-30"
                data-testid="returning-client-snooze-menu"
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-amber-800">
                    Remind me in
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowSnoozeMenu(false)}
                    className="text-zinc-400 hover:text-zinc-800"
                    aria-label="Close snooze menu"
                    data-testid="returning-client-snooze-close"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex flex-col gap-1.5">
                  {[
                    { key: "10", label: "10 days", days: 10 },
                    { key: "30", label: "30 days", days: 30 },
                    { key: "never", label: "Never", days: "never" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => {
                        writeSnooze(opt.days);
                        setShowSnoozeMenu(false);
                        setState((s) => ({ ...s, show: false }));
                      }}
                      className={`text-left w-full px-3 py-2 rounded-lg text-sm transition-colors ${
                        opt.key === "never"
                          ? "text-rose-700 hover:bg-rose-50 border border-transparent hover:border-rose-200"
                          : "text-zinc-800 hover:bg-amber-50 border border-transparent hover:border-amber-200"
                      }`}
                      data-testid={`returning-client-snooze-${opt.key}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
